import { RichTextEditor } from '../../RichTextEditor';
import type { AllReporterVariant } from '../../RichTextEditor';
import type { SlateContent } from '../types';
import { useFieldsState, useFieldsDispatch } from './Fields.Context';
import type { FieldsProviderProps } from './Fields.Context';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useReporterStyles } from 'hooks/useReporterStyles';
import { UnifiedEditor } from '../../RichTextEditor/ui/UnifiedEditor';
import { useFeatureFlagEnabled, FF } from 'modules/feature-flags';
import { RequiredFieldIndicators } from '../../RichTextEditor/ui/RequiredFieldIndicators';
import ImpressionGeneratorButton from '../../RichTextEditor/ui/ImpressionGeneratorButton';
import type { AutoloadTemplateState } from 'hooks/useAutoloadTemplate';
import { telemetryContextFor } from 'modules/Apollo/types';

export type FieldsEditorProps = Readonly<{
  onChange: (content: SlateContent) => void;
  initialContent: SlateContent;
  variant?: AllReporterVariant;
  onInit?: FieldsProviderProps['onInit'];
  onDestroy?: FieldsProviderProps['onDestroy'];
  autoloadTemplateState?: AutoloadTemplateState;
  isDisabled: boolean;
  isAddendumEditor?: boolean;
  label?: string;
  isLoading?: boolean;
}>;

export const FieldsEditor = ({
  initialContent,
  onChange,
  variant = 'report',
  isDisabled,
  onInit,
  onDestroy,
  autoloadTemplateState,
  isAddendumEditor = false,
  label,
  isLoading = false,
}: FieldsEditorProps): React.ReactElement | null => {
  const { reporterToolbarMountPoint, addendumSMID } = useFieldsState();
  const { onReportChange } = useFieldsDispatch();
  const { data } = useCurrentUser({ context: APOLLO_QUERY_CONTEXT });
  const reporterSettings = data?.me?.reporterSettings;
  const style = useReporterStyles(reporterSettings);
  const [isAutoImpressionsEnabled] = useFeatureFlagEnabled(FF.REPORTER_AUTO_IMPRESSIONS);

  // https://sironamedical.atlassian.net/browse/EN-2417
  if (initialContent == null) return null;

  return (
    <>
      <RequiredFieldIndicators />
      {isAutoImpressionsEnabled && <ImpressionGeneratorButton />}
      <UnifiedEditor
        isAddendumEditor={isAddendumEditor}
        variant={variant}
        isDisabled={variant === 'view' || isDisabled}
        label={label}
        addendumSMID={addendumSMID}
      >
        <RichTextEditor
          key={variant}
          isDisabled={variant === 'view' || isDisabled}
          isLoading={isLoading}
          variant={variant}
          style={style.bodyStyle}
          cursorStyle={reporterSettings?.cursorStyle}
          onChange={onChange}
          initialValue={initialContent}
          navMountPoint={reporterToolbarMountPoint}
          onInit={onInit}
          onDestroy={onDestroy}
          autoloadTemplateState={autoloadTemplateState}
          isAddendumEditor={isAddendumEditor}
          lineIndicator={reporterSettings?.lineIndicator}
          onReportChange={onReportChange}
        />
      </UnifiedEditor>
    </>
  );
};

FieldsEditor.displayName = 'FieldsEditor';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(FieldsEditor);
